import React from "react";

import cx from "classnames";

import AssigneeFilter, { type AssigneeType } from "~/common/AssigneeFilter";
import CycleListInput from "~/okrs/components/CycleListInput";
import ObjectiveListInput, { type ObjectiveType as ParentObjectiveType } from "~/okrs/components/ObjectiveListInput";
import { type ObjectiveAttributes, Visibility } from "~/okrs/Objective";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";

import styles from "./Settings.module.css";

interface SettingsProps {
	objective: ObjectiveAttributes;
	hasAccess: boolean;
	isCreate?: boolean;
	columnLayout?: boolean;
	allowSubmit?: boolean;
	className?: string;
	onChangeCycles(newCycle: string[]): void;
	onChangeParent(newParent: ParentObjectiveType | null): void;
	onChangeVisibility(newVisibility: Visibility): void;
	onChangeContributors(assignees: AssigneeType[]): void;
	onSubmit?(): void;
}

export default function Settings({
	objective,
	hasAccess,
	isCreate,
	columnLayout,
	allowSubmit,
	className,
	onChangeCycles,
	onChangeParent,
	onChangeVisibility,
	onChangeContributors,
	onSubmit,
}: SettingsProps) {
	const allContributors = [
		...objective.contributors.map(c => ({ user: c })),
		...objective.contributorsTeams.map(t => ({ team: t })),
	];

	return (
		<div className={cx(styles.settings, className, { [styles.columnLayout]: columnLayout })}>
			<div className={styles.setting}>
				<label>Objective cycle</label>
				<React.Suspense fallback={<LoadingSpinner inline />}>
					<CycleListInput
						selected={objective.cycle?.id ? [objective.cycle?.id] : []}
						onChange={onChangeCycles}
						className={styles.dropdown}
						disabled={!hasAccess}
						menuPlacement={isCreate ? "top" : "auto"}
					/>
				</React.Suspense>
			</div>
			<div className={styles.setting}>
				<label>Parent Objective</label>
				<React.Suspense fallback={<LoadingSpinner inline />}>
					<ObjectiveListInput
						parentingObjective={objective.id}
						selected={objective.parent}
						onChange={onChangeParent}
						className={cx(styles.dropdown, { [styles.placeholder]: !objective.parent?.id })}
						disabled={!hasAccess}
						menuPlacement={isCreate ? "top" : "auto"}
					/>
				</React.Suspense>
			</div>
			<div className={styles.setting}>
				<label>Visibility</label>
				<ArrowDropdown
					heading="Visibility"
					text={objective.visibility === Visibility.PUBLIC ? "Public" : "Private"}
					disabled={!hasAccess}
					className={styles.dropdown}
					forceCloseOnOutsideClick
				>
					{/* eslint-disable-next-line react/jsx-no-bind */}
					<DropdownItem onClick={() => onChangeVisibility(Visibility.PUBLIC)}>Public</DropdownItem>
					{/* eslint-disable-next-line react/jsx-no-bind */}
					<DropdownItem onClick={() => onChangeVisibility(Visibility.PRIVATE)}>Private</DropdownItem>
				</ArrowDropdown>
			</div>
			<div className={styles.setting}>
				<label>Editors</label>
				<AssigneeFilter
					values={allContributors}
					setValues={onChangeContributors}
					className={styles.contributorsInput}
					menuPlacement="top"
					isDisabled={!hasAccess}
					placeholder="Select editors"
				/>
			</div>
			{!isCreate && onSubmit && (
				<Button variant="primary" size="normal" onClick={onSubmit} disabled={!(allowSubmit ?? false)}>
					Save changes
				</Button>
			)}
		</div>
	);
}
